import { addDays, addWeeks, isValid, parse, startOfDay } from 'date-fns'

const WEEKDAYS = [
  ['sun', 'sunday'],
  ['mon', 'monday'],
  ['tue', 'tues', 'tuesday'],
  ['wed', 'wednesday'],
  ['thu', 'thur', 'thurs', 'thursday'],
  ['fri', 'friday'],
  ['sat', 'saturday'],
] as const

const NAMED_DATE_FORMATS = [
  'MMM d yyyy',
  'MMMM d yyyy',
  'MMM d',
  'MMMM d',
] as const

interface TimeParts {
  hours: number
  minutes: number
  remainingText: string
}

interface ParseSmartDateTimeInputOptions {
  baseDate?: Date
  now?: Date
}

const getReferenceDate = ({
  baseDate,
  now,
}: ParseSmartDateTimeInputOptions) => {
  return new Date(baseDate ?? now ?? new Date())
}

const normalizeInput = (input: string) => {
  return input.trim().replace(/\s+/g, ' ').toLowerCase()
}

const removeTextRange = (input: string, start: number, end: number) => {
  return `${input.slice(0, start)} ${input.slice(end)}`.trim()
}

const cleanDateText = (input: string) => {
  return input
    .replace(/[,.]/g, ' ')
    .replace(/\b(at|on)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const getUpcomingWeekday = (
  referenceDate: Date,
  targetDay: number,
  forceNextWeek: boolean,
) => {
  const currentDay = referenceDate.getDay()
  let daysUntilTarget = (targetDay - currentDay + 7) % 7
  if (forceNextWeek || daysUntilTarget === 0) {
    daysUntilTarget += 7
  }

  return addDays(startOfDay(referenceDate), daysUntilTarget)
}

const parseMeridiemTime = (input: string): TimeParts | null => {
  const match = /\b(\d{1,2})(?::([0-5]\d))?\s*(a|am|p|pm)\b/.exec(input)
  if (!match || match.index === undefined) {
    return null
  }

  const rawHour = Number(match[1])
  if (rawHour < 1 || rawHour > 12) {
    return null
  }

  const meridiem = match[3].startsWith('p') ? 'pm' : 'am'
  const hours =
    meridiem === 'pm'
      ? rawHour === 12
        ? 12
        : rawHour + 12
      : rawHour === 12
        ? 0
        : rawHour

  return {
    hours,
    minutes: match[2] ? Number(match[2]) : 0,
    remainingText: removeTextRange(
      input,
      match.index,
      match.index + match[0].length,
    ),
  }
}

const parseTwentyFourHourTime = (input: string): TimeParts | null => {
  const match = /\b([01]?\d|2[0-3]):([0-5]\d)\b/.exec(input)
  if (!match || match.index === undefined) {
    return null
  }

  return {
    hours: Number(match[1]),
    minutes: Number(match[2]),
    remainingText: removeTextRange(
      input,
      match.index,
      match.index + match[0].length,
    ),
  }
}

const parseBareHourTime = (input: string): TimeParts | null => {
  const match = /(?:^|\s)(\d{1,2})\s*$/.exec(input)
  if (!match || match.index === undefined) {
    return null
  }

  const rawHour = Number(match[1])
  if (rawHour < 1 || rawHour > 12) {
    return null
  }

  const hours = rawHour <= 7 ? rawHour + 12 : rawHour === 12 ? 12 : rawHour

  return {
    hours,
    minutes: 0,
    remainingText: removeTextRange(
      input,
      match.index,
      match.index + match[0].length,
    ),
  }
}

const parseTimeParts = (input: string): TimeParts | null => {
  const noonMatch = /\bnoon\b/.exec(input)
  if (noonMatch) {
    return {
      hours: 12,
      minutes: 0,
      remainingText: removeTextRange(
        input,
        noonMatch.index,
        noonMatch.index + noonMatch[0].length,
      ),
    }
  }

  const midnightMatch = /\bmidnight\b/.exec(input)
  if (midnightMatch) {
    return {
      hours: 0,
      minutes: 0,
      remainingText: removeTextRange(
        input,
        midnightMatch.index,
        midnightMatch.index + midnightMatch[0].length,
      ),
    }
  }

  return (
    parseMeridiemTime(input) ??
    parseTwentyFourHourTime(input) ??
    parseBareHourTime(input)
  )
}

const parseNumericDate = (input: string, referenceDate: Date) => {
  const isoMatch = /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/.exec(input)
  if (isoMatch) {
    const parsed = new Date(
      Number(isoMatch[1]),
      Number(isoMatch[2]) - 1,
      Number(isoMatch[3]),
    )
    return isValid(parsed) ? startOfDay(parsed) : null
  }

  const slashMatch = /\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?\b/.exec(input)
  if (!slashMatch) {
    return null
  }

  const month = Number(slashMatch[1])
  const day = Number(slashMatch[2])
  const yearText = slashMatch[3]
  const currentYear = referenceDate.getFullYear()
  const year = yearText
    ? yearText.length === 2
      ? 2000 + Number(yearText)
      : Number(yearText)
    : currentYear

  const parsed = new Date(year, month - 1, day)
  if (!isValid(parsed)) {
    return null
  }

  return startOfDay(parsed)
}

const parseNamedDate = (input: string, referenceDate: Date) => {
  for (const formatString of NAMED_DATE_FORMATS) {
    const parsed = parse(input, formatString, referenceDate)
    if (isValid(parsed)) {
      return startOfDay(parsed)
    }
  }
  return null
}

const parseWeekdayDate = (input: string, referenceDate: Date) => {
  for (let dayIndex = 0; dayIndex < WEEKDAYS.length; dayIndex += 1) {
    const aliases = WEEKDAYS[dayIndex]
    const aliasPattern = aliases.join('|')
    const nextMatch = new RegExp(`\\bnext\\s+(${aliasPattern})\\b`).exec(input)
    if (nextMatch) {
      return getUpcomingWeekday(referenceDate, dayIndex, true)
    }

    const weekdayMatch = new RegExp(`\\b(${aliasPattern})\\b`).exec(input)
    if (weekdayMatch) {
      return getUpcomingWeekday(referenceDate, dayIndex, false)
    }
  }

  return null
}

const parseDatePart = (
  input: string,
  referenceDate: Date,
  hasExplicitTime: boolean,
) => {
  if (!input || input === 'today') {
    return hasExplicitTime ? startOfDay(referenceDate) : new Date(referenceDate)
  }

  if (input === 'now') {
    return new Date(referenceDate)
  }

  if (input.includes('tomorrow')) {
    return addDays(startOfDay(referenceDate), 1)
  }

  const inDaysMatch = /\bin\s+(\d+)\s+days?\b/.exec(input)
  if (inDaysMatch) {
    return addDays(startOfDay(referenceDate), Number(inDaysMatch[1]))
  }

  const inWeeksMatch = /\bin\s+(\d+)\s+weeks?\b/.exec(input)
  if (inWeeksMatch) {
    return addWeeks(startOfDay(referenceDate), Number(inWeeksMatch[1]))
  }

  return (
    parseWeekdayDate(input, referenceDate) ??
    parseNumericDate(input, referenceDate) ??
    parseNamedDate(input, referenceDate)
  )
}

export const parseSmartDateTimeInput = (
  input: string,
  options: ParseSmartDateTimeInputOptions = {},
): Date | null => {
  const normalizedInput = normalizeInput(input)
  if (!normalizedInput) {
    return null
  }

  const referenceDate = getReferenceDate(options)

  if (normalizedInput === 'now') {
    return new Date(options.now ?? new Date())
  }

  const timeParts = parseTimeParts(normalizedInput)
  const dateText = cleanDateText(timeParts?.remainingText ?? normalizedInput)
  const parsedDate = parseDatePart(dateText, referenceDate, !!timeParts)

  if (!parsedDate) {
    return null
  }

  const result = new Date(parsedDate)
  if (timeParts) {
    result.setHours(timeParts.hours, timeParts.minutes, 0, 0)
  } else {
    result.setHours(
      referenceDate.getHours(),
      referenceDate.getMinutes(),
      referenceDate.getSeconds(),
      referenceDate.getMilliseconds(),
    )
  }

  return result
}
